import { useEffect, useRef, type ReactNode } from 'react'
import axios from 'axios'
import { useFeatureDispatch, useFeatureSelector } from '@/platform/state/hooks'
import { platformEnv } from '@/platform/config/env'
import {
  clearSessionTokens,
  getValidAccessToken,
  redirectToLogin,
  refreshAccessToken,
} from './sessionTokens'
import { sessionActions, type SessionUser } from './sessionSlice'
import { FullScreenLoader } from '@/platform/ui/FullScreenLoader'

export function SessionGate({ children }: { children: ReactNode }) {
  const dispatch = useFeatureDispatch()
  const status = useFeatureSelector((state) => (state as { session: { status: 'checking' | 'authenticated' | 'anonymous' | 'forbidden' } }).session.status)
  const initialized = useRef(false)

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const controller = new AbortController()

    const loadSession = async () => {
      // Chủ động refresh token nếu token sắp hết hạn
      const token = await getValidAccessToken()
      if (!token) return null

      try {
        return await axios.get(`${platformEnv.ssoApiUrl}/v1/auth/me`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${token}` },
        })
      } catch (cause) {
        if (!axios.isAxiosError(cause) || cause.response?.status !== 401) throw cause
        const refreshedToken = await refreshAccessToken()
        if (!refreshedToken) throw cause
        return axios.get(`${platformEnv.ssoApiUrl}/v1/auth/me`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${refreshedToken}` },
        })
      }
    }

    void loadSession()
      .then((response) => {
        if (!response) {
          dispatch(sessionActions.anonymous())
          redirectToLogin()
          return
        }
        const user = response.data?.data as SessionUser | undefined
        if (!response.data?.success || !user) throw new Error('Không đọc được phiên đăng nhập.')
        if (user.role === 'admin' || user.role === 'super_admin') dispatch(sessionActions.authenticated(user))
        else dispatch(sessionActions.forbidden(user))
      })
      .catch((cause) => {
        if (axios.isCancel(cause)) return
        if (axios.isAxiosError(cause) && cause.response?.status === 401) {
          clearSessionTokens()
          dispatch(sessionActions.anonymous())
          redirectToLogin()
        } else {
          dispatch(sessionActions.anonymous())
        }
      })
    return () => controller.abort()
  }, [dispatch])

  if (status === 'checking') return <FullScreenLoader />
  if (status === 'forbidden') return <div className="screen-center"><div className="card"><h1>Chưa có quyền quản trị</h1><p>Tài khoản này không có quyền dùng Học Nhé Editor.</p></div></div>
  if (status === 'anonymous') return <div className="screen-center"><div className="card"><h1>Không thể xác thực</h1><p>Kiểm tra kết nối rồi đăng nhập lại.</p><button className="button-primary" onClick={() => redirectToLogin()}>Đăng nhập</button></div></div>
  return <>{children}</>
}
